import { useCallback, useEffect, useState } from 'react';
import { useAutosave, type AutosaveResult } from '../../../hooks/useAutosave';

export type LoadState = 'loading' | 'ready' | 'failed';

export interface Policy<T> {
  body: T;
  patch: (p: Partial<T>) => void;
  state: LoadState;
  auto: AutosaveResult;
}

/** One settings policy: load it, hold the draft, autosave every change. A policy
 *  that failed to load is never autosaved — saving the fallback would overwrite
 *  whatever is really stored. `key` reloads (e.g. the account id).
 *  `validate` holds a save back while the draft is invalid. */
export function usePolicy<T>(load: () => Promise<T>, put: (b: T) => Promise<T | void>, fallback: T, key: string, delay?: number, validate?: (b: T) => string | null): Policy<T> {
  const [body, setBody] = useState<T>(fallback);
  const [state, setState] = useState<LoadState>('loading');
  useEffect(() => {
    let off = false;
    setState('loading');
    load().then(
      (b) => { if (!off) { setBody(b); setState('ready'); } },
      () => { if (!off) setState('failed'); },
    );
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const auto = useAutosave<T>({ value: body, save: put, enabled: state === 'ready', delay, validate });
  const patch = useCallback((p: Partial<T>) => setBody((b) => ({ ...b, ...p })), []);
  return { body, patch, state, auto };
}

/** Patch + save straight away (for toggles), skipping the typing debounce. */
export function patchNow<T>(p: Policy<T>, v: Partial<T>) {
  p.patch(v);
  window.setTimeout(p.auto.flush, 0);
}
